import { useEffect, useState } from "react";
import { Formik } from "formik";
import SelectDropdown from "../../components/form-control/SelectDropdown";
import Button from "../../components/form-control/Button";
import { setAgentTarget, updateAgentTarget } from "../../api/requests";
import Input from "../../components/form-control/Input";
import { getSelectItemsFromEnum } from "../../utilities/miscFunctions";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import * as Yup from "yup";
import { AgentTarget } from "../../resources/types";
import { AGENT_TARGET_PERIOD } from "../../resources/enums";
import MultiSelect, { IMultiSelectItem } from "../../components/form-control/MultiSelect";
import { upperCase } from "lodash";
import moment from "moment";
import useUsers from "../../hooks/UsersHook";
import TextArea from "../../components/form-control/TextArea";
import DatePickerInput from "../../components/form-control/DatePicker/DatePickerInput";
import { ISetAgentTargetDTO } from "../../resources/interfaces";

interface IProps {
    onComplete: (agentTargets: AgentTarget[]) => void;
    agentTarget?: AgentTarget;
    isEdit?: boolean;
    branchId?: string;
}

const SetAgentTarget = (props: IProps): JSX.Element => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const { USERS_LIST_FOR_FORM, fetchUsers } = useUsers();

    useEffect(() => {
        fetchUsers({ branchId: props.branchId, isActive: true });
    }, [props.branchId])

    const initialValues = {
        users: props.agentTarget ? [props.agentTarget.user_id] : [],
        deposit_amount_target: 0,
        deposit_count_target: 0,
        customer_target: 0,
        period: props.agentTarget?.period ? new Date(props.agentTarget?.period) : new Date(),
        notes: props.agentTarget?.notes ?? ""
    }

    const submitHandler = (values: ISetAgentTargetFormData) => {
        const payload: ISetAgentTargetDTO = {
            ...values, 
            period: moment(values.period).format("YYYY-MM")
        };

        setSubmitting(true);
        if (props.isEdit && props.agentTarget?.id) {
            updateAgentTarget(props.agentTarget.id, payload)
                .then((response: any) => {
                    if (response) {
                        props.onComplete(response);
                    }
                    toast.success(`"Agent target updated successfully"`);
                }).catch((error: any | AxiosError | Error) => {
                    toast.error(error?.description, { autoClose: 5000 });
                    console.log("error response", error);
                }).finally(() => setSubmitting(false))
        } else {
            setAgentTarget(payload)
                .then((response: any) => {
                    if (response) {
                        props.onComplete(response);
                    }
                    toast.success(`"Agent target created successfully"`);
                }).catch((error: any | AxiosError | Error) => {
                    toast.error(error?.description, { autoClose: 5000 });
                    console.log("error response", error);
                }).finally(() => setSubmitting(false))
        }
    }

    return (
        <div>
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object({
                    deposit_amount_target: Yup.number()
                        .required('Deposit amount target is required'),
                    deposit_count_target: Yup.number()
                        .required('Deposit count target is required'),
                    customer_target: Yup.number()
                        .required('Customer target is required'),
                    period: Yup.string()
                        .trim()
                        .required('Target period is required'),
                    users: Yup.array()
                        .required('Agents is required')
                })}
                onSubmit={(values: ISetAgentTargetFormData) => {
                    submitHandler(values);
                }}>
                {({ values, handleBlur, handleChange, handleSubmit, setFieldValue, touched, errors }) => (
                    <form onSubmit={handleSubmit} className="mt-4">
                        <div className="flex flex-col gap-4">
                            <MultiSelect
                                label="Select Agents"
                                onChange={(selected: IMultiSelectItem[]) => {
                                    console.log("users", selected);
                                    setFieldValue("users", selected);
                                }}
                                list={USERS_LIST_FOR_FORM as IMultiSelectItem[]}
                                selected={values.users}
                                selectInputClassName=""
                                error={touched?.users ? errors.users?.[0] : undefined}
                                helperText={touched?.users ? errors.users?.[0] : undefined}
                                required
                            />

                            <Input
                                type="number"
                                value={values.deposit_amount_target}
                                name="deposit_amount_target"
                                label="Deposit amount target"
                                placeholder="Enter deposit amount target"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.deposit_amount_target ? errors.deposit_amount_target : undefined
                                }
                                helperText={touched?.deposit_amount_target ? errors.deposit_amount_target : undefined
                                }
                                required
                                min={0}
                            />

                            <Input
                                type="number"
                                value={values.deposit_count_target}
                                name="deposit_count_target"
                                label="Deposit Count Target Amount"
                                placeholder="Enter deposit count target"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.deposit_count_target ? errors.deposit_count_target : undefined
                                }
                                helperText={touched?.deposit_count_target ? errors.deposit_count_target : undefined
                                }
                                required
                                min={0}
                            />

                            <Input
                                type="number"
                                value={values.customer_target}
                                name="customer_target"
                                label="Customer Target"
                                placeholder="Enter customer target"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.customer_target ? errors.customer_target : undefined
                                }
                                helperText={touched?.customer_target ? errors.customer_target : undefined
                                }
                                required
                                min={0}
                            />

                            <DatePickerInput
                                id="period"
                                selected={values.period}
                                onChange={(date: Date | null) => {
                                    console.log("date", date);
                                    setFieldValue("period", date);
                                }}
                                dateFormat="MM-yyyy"
                                label="Target Period"
                                showMonthYearPicker
                                placeholder="Select target period"
                                required
                                error={touched.period && errors.period !== undefined ? errors.period : undefined}
                                helperText={touched.period ? errors.period : undefined}
                            />

                            {/* <SelectDropdown
                                list={getSelectItemsFromEnum(AGENT_TARGET_PERIOD)}
                                listSelectedValue={values.period}
                                label="Period"
                                placeholder="Select period"
                                wrapperClasses="rounded"
                                onBlur={handleBlur}
                                onChange={(value: string) => {
                                    setFieldValue("period", value);
                                }}
                                required
                                error={touched.period ? errors.period : undefined
                                }
                                helperText={touched?.period ? errors.period : undefined
                                }
                            /> */}

                            <TextArea
                                value={values.notes}
                                name="notes"
                                label="Notes"
                                placeholder="Enter notes"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.notes ? errors.notes : undefined
                                }
                                helperText={touched?.notes ? errors.notes : undefined
                                }
                                rows={2}
                            />

                        </div>

                        <div className="flex justify-end mt-8">
                            <Button
                                label="Save"
                                type="submit"
                                disabled={submitting}
                                loading={submitting}
                                className="px-12 bg-secondary-main rounded hover:bg-white hover:border-secondary-main"
                            />
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    )
}

export default SetAgentTarget;

export interface ISetAgentTargetFormData {
    users: string[];
    deposit_amount_target: number;
    deposit_count_target: number;
    customer_target: number;
    period: Date | null;
    notes: string;
}